/**
 * Data Mapper
 *
 * Converts between backend API types and frontend domain types
 */

import type {
  JobFunction as BackendJobFunction,
  JobLevel as BackendJobLevel,
  JobCard,
  GridConfiguration,
} from '../types/api';
import type {
  Card,
  JobFunction as FrontendJobFunction,
  JobFunctionGroup as FrontendJobFunctionGroup,
  CareerTrack as FrontendCareerTrack
} from '../types';

/**
 * Convert backend JobFunction to frontend JobFunction
 */
export function mapBackendJobFunctionToFrontend(func: BackendJobFunction): FrontendJobFunction {
  return {
    id: `func-${func.id}`,
    label: func.name,
    order: func.sort_order,
  };
}

/**
 * Convert backend JobLevel to frontend level string
 */
export function mapBackendJobLevelToFrontend(level: BackendJobLevel): string {
  return level.name;
}

/**
 * Convert backend JobCard to frontend Card
 * Note: Backend uses (job_function_id, job_level_id), frontend uses (functionId, rowIndex)
 */
export function mapJobCardToCard(
  card: JobCard,
  functionIdToFrontendId: Map<number, string>,
  levelIdToRowIndex: Map<number, number>
): Card {
  const functionId = functionIdToFrontendId.get(card.job_function_id) || `func-${card.job_function_id}`;
  const rowIndex = levelIdToRowIndex.get(card.job_level_id) ?? 0;

  return {
    id: `card-${card.id}`,
    content: card.title,
    functionId,
    rowIndex,
    subJobs: card.sub_jobs || undefined,
  };
}

/**
 * Build mapping from job_function_id to functionId
 */
export function buildJobFunctionToFrontendMap(functions: BackendJobFunction[]): Map<number, string> {
  const map = new Map<number, string>();
  functions.forEach(func => {
    map.set(func.id, `func-${func.id}`);
  });
  return map;
}

/**
 * Build mapping from job_level_id to rowIndex (based on sort_order)
 */
export function buildLevelToRowIndexMap(levels: BackendJobLevel[]): Map<number, number> {
  const map = new Map<number, number>();
  const sortedLevels = [...levels].sort((a, b) => a.sort_order - b.sort_order);
  sortedLevels.forEach((level, index) => {
    map.set(level.id, index);
  });
  return map;
}

/**
 * Extract backend IDs from frontend IDs
 */
export function extractJobFunctionId(functionId: string): number | null {
  const match = functionId.match(/^func-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

export function extractCardId(cardId: string): number | null {
  const match = cardId.match(/^card-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Convert frontend functionId and rowIndex to backend job_function_id and job_level_id
 */
export function mapFrontendPositionToBackend(
  functionId: string,
  rowIndex: number,
  functionIdToJobFunctionId: Map<string, number>,
  rowIndexToLevelId: Map<number, number>
): { job_function_id: number; job_level_id: number } | null {
  const job_function_id = functionIdToJobFunctionId.get(functionId);
  const job_level_id = rowIndexToLevelId.get(rowIndex);

  if (job_function_id === undefined || job_level_id === undefined) {
    return null;
  }

  return { job_function_id, job_level_id };
}

/**
 * Build reverse mapping from functionId to job_function_id
 */
export function buildFrontendToJobFunctionMap(functions: BackendJobFunction[]): Map<string, number> {
  const map = new Map<string, number>();
  functions.forEach(func => {
    map.set(`func-${func.id}`, func.id);
  });
  return map;
}

/**
 * Build reverse mapping from rowIndex to job_level_id
 */
export function buildRowIndexToLevelMap(levels: BackendJobLevel[]): Map<number, number> {
  const map = new Map<number, number>();
  const sortedLevels = [...levels].sort((a, b) => a.sort_order - b.sort_order);
  sortedLevels.forEach((level, index) => {
    map.set(index, level.id);
  });
  return map;
}

/**
 * Transform backend GridConfiguration into frontend format
 * Groups job functions by their group_id, creates frontend JobFunctionGroups
 */
export function transformGridConfiguration(config: GridConfiguration): {
  jobFunctionGroups: FrontendJobFunctionGroup[];
  levels: string[];
  careerTracks: FrontendCareerTrack[];
} {
  // Sort groups by sort_order
  const sortedGroups = [...config.job_function_groups].sort((a, b) => a.sort_order - b.sort_order);

  // Sort functions by sort_order
  const sortedFunctions = [...config.job_functions].sort((a, b) => a.sort_order - b.sort_order);

  // Build frontend job function groups with embedded functions
  const jobFunctionGroups: FrontendJobFunctionGroup[] = sortedGroups.map((backendGroup, groupIndex) => {
    // Find all functions belonging to this group
    const groupFunctions = sortedFunctions
      .filter(f => f.job_function_group_id === backendGroup.id)
      .map((func, funcIndex) => ({
        id: `func-${func.id}`,
        label: func.name,
        order: funcIndex,
      }));

    return {
      id: `group-${backendGroup.id}`,
      label: backendGroup.name,
      functions: groupFunctions,
      order: groupIndex,
    };
  });

  // Sort levels by career track sort_order, then by level sort_order within each track
  // If a career track has no levels, insert a placeholder
  const sortedCareerTracks = [...config.career_tracks].sort((a, b) => a.sort_order - b.sort_order);
  const levels: string[] = [];

  for (const track of sortedCareerTracks) {
    const trackLevels = config.job_levels
      .filter(level => level.career_track_id === track.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(level => level.name);

    if (trackLevels.length === 0) {
      // Empty track - add placeholder
      levels.push(`(Empty - add level to ${track.name})`);
    } else {
      levels.push(...trackLevels);
    }
  }

  // Sort career tracks by sort_order and convert to frontend format
  // Calculate rowspan dynamically by counting levels that belong to each track
  // Empty tracks get rowspan of 1 for the placeholder
  const careerTracks: FrontendCareerTrack[] = [...config.career_tracks]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(track => {
      // Count how many levels belong to this career track
      const levelCount = config.job_levels.filter(level => level.career_track_id === track.id).length;
      return {
        label: track.name,
        rowspan: levelCount === 0 ? 1 : levelCount, // Empty tracks get rowspan 1 for placeholder
      };
    });

  return {
    jobFunctionGroups,
    levels,
    careerTracks,
  };
}
